import React, { useState, useEffect } from 'react';
import utils from '../../common/utils.js';
import config from '../../common/config.js';
import metrics from '../services/metrics.js';

const PostFields = {};

const getLinkNameForUrl = (url) => {
	url = utils.cleanString(url);
	let linkName = '';
	let fileName = url.replace(/^.*\//, '');
	let description = utils.getFileDescription(url);
	if (description) {
		linkName = description;
	} else {
		let name = fileName.replace(/\..*$/, '');
		linkName = utils.titleCase(name.split(/[\-_\s]+/).join(' '));
	}
	return linkName;
};

/**
 * Render a title with appropriate icons for the given url
 */
PostFields.title = (props) => {
	// grab template version from widgetModel
	const tpVersion = props?.widgetModel?.tpVersion || 4;
	const noIcon = !!props.hasOwnProperty('noIcon');

	let url = (props.url || props.link || props.href) ? utils.cleanUrl(props.url || props.link || props.href) : '';
	const [linkText, setLinkText] = useState('');

	let isExternal = utils.urlIsExternal(url);
	let icon = utils.getIconForUrl(url);
	let iconTP5 = utils.getTP5IconForUrl(url);
	let classes = [];

	if (isExternal) {
		classes.push('tp-link-policy');
	}
	if (props.className) {
		classes.push(props.className);
	}

	useEffect(() => {
		props?.text && setLinkText(utils.cleanHTML(props?.text));
	}, [props.text]);

	return (
		<>
			{(icon && !noIcon && 4 === tpVersion) ? (
				<span className={`cdc-icon-${icon} x16 fill-${icon}`} role="presentation" aria-hidden="true"></span>
			):''}
			{linkText ? <span dangerouslySetInnerHTML={{ __html: linkText }}></span> : ''}
			{props.children}

			{(iconTP5 && !noIcon && 5 === tpVersion) ? (
				<span className={`cdc-link-icon cdc-link-icon--${iconTP5}`} role="presentation" aria-hidden="true"></span>
			):''}

			{isExternal && !noIcon && 4 === tpVersion ? (
				<>
					<span aria-label="external icon" className="sr-only" role="img"></span>
					<span className="fi cdc-icon-external x16 fill-external" alt="" aria-hidden="true"></span>
				</>
			) : (
				''
			)}
			{isExternal && !noIcon && 5 === tpVersion ? (
				<span alt="external icon" className="cdc-icon-external cdc-link-icon" aria-hidden="true"></span>
			) : (
				''
			)}
		</>
	);
}

/**
 * Handles outputting links with decorations
 * - props
 * 		- text: if you have HTML in your link text
 * 		- url: target url
 * 		- target: link target
 */
PostFields.link = (props) => {
	let url = (props.url || props.link || props.href) ? utils.cleanUrl(props.url || props.link || props.href) : '';
	let target = utils.cleanString(props.target || '');

	const [linkText, setLinkText] = useState('');
	const [ariaLabel, setAriaLabel] = useState('');

	// file urls force _blank target
	if (utils.isFileUrl(url) || utils.urlIsNewTab(url)) {
		target = '_blank';
	}

	let isExternal = utils.urlIsExternal(url);
	let classes = [];

	if (isExternal) {
		classes.push('tp-link-policy');
	}
	if (props.className) {
		classes.push(props.className);
	}

	useEffect(() => {
		props?.text && setLinkText(utils.cleanHTML(props?.text));
	}, [props.text]);

	useEffect(() => {
		props?.lang && setAriaLabel({ 'aria-label': `is available in ${props.lang}` });
	}, [props.lang, linkText]);

	const content = (
		<PostFields.title { ...props}></PostFields.title>
	);
	if (!url) {
		return content;
	}
	return (
		<a
			href={url}
			className={classes.join(' ')}
			target={target}
			onClick={(e) => metrics.clickTrack(e, 'cc-link', 'click')}
			{...ariaLabel}
		>
			{content}
		</a>
	);
};

/**
 * Handles outputting images
 * Props:
 *   - src: image source
 *   - alt: image alt
 *   - link: any link for the image
 *   - hideMobile: hide images on mobile
 */
PostFields.image = (props) => {
	let src = utils.cleanUrl(props.src);
	let alt = utils.escapeString(props.alt, true);
	let link = props.link;

	// options
	let hideMobile = Boolean(props.hideMobile);

	let imageClassName = 'post-image' + (hideMobile ? ' hide-image-mobile' : '');
	return (
		<>
			{link ? (
				<PostFields.link href={link} noIcon>
					<img src={src} alt={alt} className={imageClassName} />
				</PostFields.link>
			) : (
				<img src={src} alt={alt} className={imageClassName} />
			)}
		</>
	);
};

/**
 * Render additional fields
 */
PostFields.addtlFields = (props) => {
	const postModel = props?.postModel;
	const data = props?.data;
	if (!postModel) {
		return '';
	}
	return (
		<div className="post-addtl-fields">
			<PostFields.altFormats { ...props} altFormats={postModel.altFormats}></PostFields.altFormats>
			<PostFields.altLanguages { ...props} altLanguages={postModel.altLanguages}></PostFields.altLanguages>
			<PostFields.relatedContent { ...props} relatedContent={postModel.relatedContent}></PostFields.relatedContent>
			<PostFields.filters { ...props} filters={postModel.filters} data={data}></PostFields.filters>
		</div>
	);
};

PostFields.relatedContent = (props) => {
	const relatedPages = props?.relatedContent;
	if( !relatedPages || !relatedPages.display){
		return ''
	}
	const label = utils.cleanString(relatedPages.label);
	const links = utils.toArray(relatedPages.value);
	if (!links.length) {
		return '';
	}

	return (
		<div className="post-addtl-field post-relatedContent">
			{label ? <span className="field-label">{label}: </span> : ''}
				{links
					.map((link, index) => {
						let text = utils.cleanString(link.text);
						if (!text) {
							text = getLinkNameForUrl(link.url);
						}
						return (
							<PostFields.link href={link.url} key={index}>
								{text}
							</PostFields.link>
						);
					})
					.reduce((y, x) => (null === y ? [x] : [y, ', ', x]), null)}
		</div>
	)
}

/**
 * Render altFormats field
 */
PostFields.altFormats = (props) => {
	const altFormats = props?.altFormats;
	if (!altFormats || !altFormats.display) {
		return '';
	}
	const label = utils.cleanString(altFormats.label);
	const links = utils.toArray(altFormats.value);

	if (!links.length) {
		return '';
	}

	return (
		<div className="post-addtl-field post-altformats">
			{label ? <span className="field-label">{label}: </span> : ''}
			{links
				.map((link, index) => {
					let text = utils.cleanString(link.text);
					if (!text) {
						text = getLinkNameForUrl(link.url);
					}
					return (
						<PostFields.link { ...props} href={link.url} lang={text} key={index}>
							{text}
						</PostFields.link>
					);
				})
				.reduce((y, x) => (null === y ? [x] : [y, ', ', x]), null)}
		</div>
	);
};

/**
 * Render altLanguages field
 */
PostFields.altLanguages = (props) => {
	const altLanguages = props?.altLanguages;
	if (!altLanguages || !altLanguages.display) {
		return '';
	}
	const label = utils.cleanString(altLanguages.label);
	const links = utils.toArray(altLanguages.value);

	if (!links.length) {
		return '';
	}

	return (
		<div className="post-addtl-field post-altlanguages">
			{label ? <span className="field-label">{label}: </span> : ''}
			{links
				.map((link, index) => {
					let text = link.text;
					let lang = utils.getLanguage(link.lang);
					if (lang && !text) {
						text = lang;
					}
					if (!text) {
						text = getLinkNameForUrl(link.url);
					}

					return (
						<PostFields.link href={link.url} key={index} lang={lang}>
							{text}
						</PostFields.link>
					);
				})
				.reduce((y, x) => (null === y ? [x] : [y, ', ', x]), null)}
		</div>
	);
};

/**
 * Display post filter tags
 */
PostFields.filters = (props) => {
	const filters = Object.assign({}, props?.filters);
	const data = props.data;
	let out = [];
	for (let filterId in filters) {
		let filter = filters[filterId];
		if (!filter?.display || !Array.isArray(filter.value)) {
			continue;
		}
		out.push(
			<div key={'post-filter-' + filterId} className={'post-addtl-field post-filter post-filter-' + filterId}>
				<span className="field-label">{filter.label}: </span>
				{filter.value
					.map((option,i) => {
						return (
							<button
								key={'post-filter-option-' + option.value}
								className="post-filter-option"
								onClick={(e) => {
									let filterValues = {};
									filterValues[filterId] = option;
									data.setFilterValues(filterValues);
									data.setPage(1);
									data.getPosts();
								}}
							>
								{option.label}
							</button>
						);
					})
					.reduce(
						(acc, x) =>
							null === acc
								? [x]
								: [
										acc,
										<span key={x} className="option-break">
											,{' '}
										</span>,
										x,
								  ],
						null
					)}
			</div>
		);
	}
	return out;
};

/**
 * Render WCMS edit link for posts in preview
 */
PostFields.editLink = (props) => {
	const widgetModel = props?.widgetModel;
	const postModel = props?.postModel;
	if (!config.wcmsHost || !widgetModel?.inEditor) {
		return '';
	}
	let url = postModel.url.value;
	let folders = utils.parseUrl(url).pathname.replace(/^\//, '').split('/');
	let siteFolder = folders.shift();
	let postId = utils.toNumber(String(postModel.id).replace(/^.*_/, ''));
	if (!url || !postId || !siteFolder) {
		return '';
	}
	let editUrl = '//' + config.wcmsHost + '/' + siteFolder + '/wp-admin/post.php?post=' + postId + '&action=edit';
	return (
		<span className="edit-link">
			<a href={editUrl} target="_blank" rel="noreferrer" className="cdc-button button button-primary">
				<span className="cdc-icon cdc-icon-external"></span>
				Edit in WCMS
			</a>
		</span>
	);
};
PostFields.downloadButton = (props) => {
	let url = (props.url || props.link || props.href) ? utils.cleanUrl(props.url || props.link || props.href) : '';
	let isExternal = utils.urlIsExternal(url);
	const handleClick = ( e ) => {
		e.preventDefault();
		utils.downloadFile(url);
	}
	if (isExternal) {
		classes.push('tp-link-policy');
	}
	return (
		<button className="cdc-button download-button" onClick={handleClick}>
			<span class="cdc-fa-arrow-down-to-line"></span>
			&nbsp;
			<span class="download-text">Download</span>
		</button>
	);
}


/**
 * Empty result set
 */
PostFields.noResults = (props) => {
	return <div className="no-results">No results available for the selected criteria.</div>;
};

/**
 * Utils for common post fields
 */
export default PostFields;
